<template>
  <!--
    SHARED SECTION BODY — the "Data Source" tab body reused by every artifact that
    has a join graph (metric DataSourceSection, FlagDataSourceSection, worklist,
    insight). It is NOT a registry section itself; the per-artifact Data Source
    sections wrap it and own the record↔studio_config mapping.

    WHAT: Wraps builder/StudioJoinBuilder (reused verbatim from Metric Studio v1 —
      step chain + join-clause editor + output-contract checklist + combined-SQL
      preview), and translates the builder's v-model into an `update:value` the
      parent section turns into a `patch`.

    CONTRACT (this is a plain child component, not a { record, artifact, context }
      section — it takes explicit props):
      READS  value (the studio_config { steps, primary_key_columns,
             excluded_columns }), attributes (DA catalog), sqlUrl (POST endpoint
             for combined SQL; null → renders a step-list stub instead),
             sqlFilters, excludedColumns, showContract, configTabLabel.
      EMITS  update:value (a deep clone of the normalized studio_config),
             update:excludedColumns, and attribute-saved (bubbled when a DA is
             edited in the builder).
      NOTE: it does NOT emit `patch`/`refresh` itself — the wrapping Data Source
            section merges update:value into the record and re-emits patch.

    NOTES:
      - normalize() shapes each step to { workflow_id, role, join_type, join_on_sql,
        note } and defaults primary_key_columns to ['BENE_MBI_ID'].
      - hydrating guard: set false on $nextTick after mount AND re-armed by the
        deep `value` watcher (only when the incoming value actually differs) so
        external reloads don't echo back an update.
      - Lazily caches output columns via GET /api/metrics/workflow-columns/{workflow_id}/
        (columnsCache) to build the SQL-preview column list (minus excluded).
      - Slots #config straight through to StudioJoinBuilder for the artifact's
        own extra config panel.
  -->
  <div>
    <StudioJoinBuilder
      v-model="cfg"
      v-model:excluded-columns="excluded"
      :attributes="attributes"
      :columns-cache="columnsCache"
      :sql="combinedSql"
      :show-contract="showContract"
      :output-contract="outputContract"
      :identifier-columns="groupByColumns"
      :grain="grain"
      @update:identifier-columns="$emit('update:groupByColumns', $event)"
      @request-columns="(id) => fetchColumns(id, true)"
      @refresh-sql="fetchSql"
      @attribute-saved="onAttributeSaved"
    >
      <template v-if="$slots.config" #config="slotProps">
        <slot name="config" v-bind="slotProps" />
      </template>
    </StudioJoinBuilder>
  </div>
</template>

<script>
import axios from 'axios';
import StudioJoinBuilder from '@/components/Admin/MetricStudio/builder/StudioJoinBuilder.vue';

export default {
  name: 'JoinGraphSection',
  components: { StudioJoinBuilder },
  props: {
    // the studio_config ({ steps:[...], primary_key_columns:[...] })
    value: { type: Object, default: () => ({ steps: [], primary_key_columns: ['BENE_MBI_ID'] }) },
    attributes: { type: Array, default: () => [] },
    // POST endpoint that returns { sql } for the combined preview; null = skip
    sqlUrl: { type: String, default: null },
    sqlFilters: { type: Object, default: null },
    excludedColumns: { type: Array, default: () => [] },
    showContract: { type: Boolean, default: false },
    configTabLabel: { type: String, default: '' },
    contractConfig: { type: Object, default: null },
    groupByColumns: { type: Array, default: () => [] },
    // pass-through to StudioJoinBuilder's contract checklist ('npi' | 'entity').
    // Grain OWNERSHIP stays with the Aggregation section — normalize() here
    // deliberately does not carry it, so this section can never clobber it.
    grain: { type: String, default: 'npi' },
  },
  emits: ['update:value', 'update:excludedColumns', 'update:groupByColumns', 'attribute-saved'],
  data() {
    return {
      cfg: this.normalize(this.value),
      excluded: [...(this.excludedColumns || [])],
      columnsCache: {},
      combinedSql: '',
      outputContract: {},
      hydrating: true,
    };
  },
  watch: {
    value: {
      deep: true,
      handler(v) {
        const next = this.normalize(v);
        if (JSON.stringify(next) === JSON.stringify(this.cfg)) return;
        this.hydrating = true;
        this.cfg = next;
        this.$nextTick(() => { this.hydrating = false; });
      },
    },
    cfg: { deep: true, handler() { this.onChange(); } },
    contractConfig: { deep: true, handler() { this.fetchSql(); } },
    groupByColumns: { deep: true, handler() { this.fetchSql(); } },
    excluded: { deep: true, handler() { this.$emit('update:excludedColumns', [...this.excluded]); } },
  },
  async mounted() {
    const ids = (this.cfg.steps || []).map((s) => s.workflow_id).filter(Boolean);
    await Promise.all(ids.map((id) => this.fetchColumns(id)));
    await this.fetchSql();
    this.$nextTick(() => { this.hydrating = false; });
  },
  methods: {
    normalize(v) {
      const sc = v || {};
      return {
        steps: (sc.steps || []).map((s) => ({
          workflow_id: s.workflow_id,
          role: s.role || '',
          join_type: s.join_type || null, join_on_sql: s.join_on_sql || '',
          note: s.note || '',
        })),
        primary_key_columns: [...(sc.primary_key_columns || ['BENE_MBI_ID'])],
      };
    },
    onChange() {
      if (this.hydrating) return;
      for (const s of this.cfg.steps || []) {
        if (s.workflow_id && !this.columnsCache[s.workflow_id]) this.fetchColumns(s.workflow_id);
      }
      this.$emit('update:value', JSON.parse(JSON.stringify(this.cfg)));
    },
    async fetchColumns(workflowId, force = false) {
      if (this.columnsCache[workflowId] && !force) return this.columnsCache[workflowId];
      try {
        const { data } = await axios.get(`/api/metrics/workflow-columns/${workflowId}/`);
        this.columnsCache = { ...this.columnsCache, [workflowId]: data.columns || [] };
      } catch {
        if (!this.columnsCache[workflowId]) this.columnsCache = { ...this.columnsCache, [workflowId]: [] };
      }
      return this.columnsCache[workflowId];
    },
    async fetchSql() {
      if (!this.sqlUrl) {
        const steps = (this.cfg.steps || [])
          .map((s, i) => `${String.fromCharCode(97 + i)}: workflow #${s.workflow_id} (${s.role || 'fact'})`).join('\n');
        this.combinedSql = steps ? `-- studio_config steps\n${steps}` : '-- No steps defined';
        return;
      }
      try {
        const cols = [];
        const seen = new Set();
        const excluded = new Set(this.excluded || []);
        for (const s of this.cfg.steps || []) {
          for (const c of this.columnsCache[s.workflow_id] || []) {
            if (!seen.has(c)) { seen.add(c); if (!excluded.has(c)) cols.push(c); }
          }
        }
        const body = {
          columns: cols.length ? cols : undefined,
          studio_config: this.contractConfig || this.value,
          group_by_columns: this.groupByColumns,
        };
        if (this.sqlFilters) body.filters = this.sqlFilters;
        const { data } = await axios.post(this.sqlUrl, body);
        this.combinedSql = data.sql || '';
        this.outputContract = data.output_contract || {};
      } catch (e) {
        this.combinedSql = `-- SQL preview unavailable: ${e?.response?.data?.error || e.message}`;
      }
    },
    onAttributeSaved(attr) {
      this.$emit('attribute-saved', attr);
      if (attr && attr.workflow_id) this.fetchColumns(attr.workflow_id, true);
    },
  },
};
</script>
